import React, { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { AuthError, AuthErrorCodes } from "firebase/auth";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "components/ui/card";
import { Logo } from "components/logo";

export interface ILoginLinkErrorProps {
  error: AuthError;
}

export const AccountVerificationError = "auth/error-code:-47";

export function LoginLinkError({ error }: ILoginLinkErrorProps) {
  const { t } = useTranslation(["login", "general"]);

  const { title, message } = useMemo(() => {
    if (error.code === AuthErrorCodes.INVALID_OOB_CODE) {
      return {
        title: t("login:link.redirect.error.link-expired.title"),
        message: t("login:link.redirect.error.link-expired.message"),
      };
    }

    if (error.code === AccountVerificationError) {
      return {
        title: t("login:link.redirect.error.unregistered-user.title"),
        message: t("login:link.redirect.error.unregistered-user.message"),
      };
    }

    return {
      title: t("login:link.redirect.error.unknown-error.title"),
      message: t("login:link.redirect.error.unknown-error.message"),
    };
  }, [t, error]);

  return (
    <div className="flex flex-col h-screen bg-tertiary-50 items-center justify-center">
      <div className="absolute top-8 left-8">
        <Logo variant="serif" size="md" />
      </div>
      <Card className="w-full max-w-md bg-warm-white rounded-xl shadow-card border-0">
        <CardContent className="flex flex-col items-center justify-center p-10 space-y-4">
          <div className="p-3 bg-system-error/10 rounded-full">
            <AlertTriangle
              aria-hidden="true"
              className="h-8 w-8 text-system-error"
            />
          </div>
          <div className="text-center space-y-2">
            <h1 className="font-semibold text-2xl text-tertiary-900">
              {title}
            </h1>
            <p className="text-tertiary-600">{message}</p>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-xl bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 w-full mt-4"
          >
            {t("login:link.redirect.error.actions.login")}
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
